import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  DndContext, closestCorners, pointerWithin, PointerSensor, KeyboardSensor, useSensor, useSensors, useDroppable, DragOverlay,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Upload, Loader2, Plus, Trash2, RotateCw, RotateCcw, Package, Layers, ArrowDownToLine, Wand2, Save,
  AlertTriangle, GripVertical, Container as ContainerIcon, X, ShieldCheck, FolderOpen, Columns2, Search,
} from "lucide-react";
import LoadDiagram, { buildColorMap } from "./loadDiagram";
import { QualityCard, WeightDistribution, KG_TO_LB } from "./planQualityUI";
import { PlacementAuditor } from "./placementAuditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
let _uid = 0;
const uid = () => `p${++_uid}`;
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

function Meter({ label, pct, over }) {
  const p = Math.min(100, Math.max(0, pct || 0));
  const bar = over ? "bg-red-500" : p > 92 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between text-[10px] text-slate-500"><span>{label}</span><span className={over ? "text-red-600 font-semibold" : ""}>{Math.round(pct || 0)}%</span></div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${p}%` }} /></div>
    </div>
  );
}

function PalletCard({ id, data, colorOf, onRotate, onPeel, onRemove, inTray, count }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const stackDrop = useDroppable({ id: `stack:${id}`, disabled: inTray });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const code = data.code;
  const layers = data.layers?.length || 1;
  return (
    <div ref={setNodeRef} style={style} className="rounded-md border border-slate-200 bg-white shadow-sm" data-testid={`pallet-card-${id}`}>
      <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs">
        <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none" data-testid={`pallet-drag-${id}`}><GripVertical className="w-3.5 h-3.5" /></span>
        <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: colorOf(code) }} />
        <span className="font-medium text-slate-700 truncate flex-1">{code}</span>
        {count > 1 && <Badge className="h-5 px-1.5 text-[11px] bg-slate-800 text-white shrink-0" data-testid={`pallet-count-${id}`}>{count}</Badge>}
        {layers > 1 && <Badge variant="outline" className="h-4 px-1 text-[10px] border-slate-300">×{layers}</Badge>}
        {data.rotated && <RotateCw className="w-3 h-3 text-blue-500" title="Rotated 90°" />}
        {!inTray && (
          <>
            <button onClick={onRotate} title="Rotate 90°" className="text-slate-400 hover:text-blue-600 p-0.5" data-testid={`pallet-rotate-${id}`}><RotateCw className="w-3.5 h-3.5" /></button>
            {layers > 1 && <button onClick={onPeel} title="Peel top pallet to tray" className="text-slate-400 hover:text-amber-600 p-0.5" data-testid={`pallet-peel-${id}`}><Layers className="w-3.5 h-3.5" /></button>}
          </>
        )}
        <button onClick={onRemove} title={inTray ? "Discard" : "Send to tray"} className="text-slate-400 hover:text-red-600 p-0.5" data-testid={`pallet-remove-${id}`}>{inTray ? <X className="w-3.5 h-3.5" /> : <ArrowDownToLine className="w-3.5 h-3.5" />}</button>
      </div>
      {!inTray && (
        <div ref={stackDrop.setNodeRef} className={`text-[10px] text-center rounded-b-md border-t border-dashed py-0.5 transition-colors ${stackDrop.isOver ? "bg-blue-100 border-blue-400 text-blue-700 font-medium" : "border-slate-200 text-slate-300"}`} data-testid={`pallet-stack-zone-${id}`}>
          <Layers className="w-3 h-3 inline mr-0.5" />{stackDrop.isOver ? "drop to stack on top" : "stack here"}
        </div>
      )}
    </div>
  );
}

function LaneEnd({ listKey }) {
  const { setNodeRef, isOver } = useDroppable({ id: `${listKey}::end` });
  return (
    <div ref={setNodeRef} className={`text-[10px] text-center rounded-md border border-dashed py-1 transition-colors ${isOver ? "bg-blue-100 border-blue-400 text-blue-700 font-medium" : "border-slate-200 text-slate-300"}`} data-testid={`lane-end-${listKey}`}>
      {isOver ? "drop to place at the end" : "＋ end of lane"}
    </div>
  );
}

function Lane({ listKey, ids, data, colorOf, label, onRotate, onPeel, onRemove, inTray, groups }) {
  const { setNodeRef, isOver } = useDroppable({ id: listKey });
  const items = groups ? groups.map((g) => g.rep) : ids;
  return (
    <div ref={setNodeRef} className={`rounded-lg border p-2 min-h-[80px] transition-colors ${isOver ? "border-blue-400 bg-blue-50/60" : "border-slate-200 bg-slate-50/40"}`} data-testid={`lane-${listKey}`}>
      {label && <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">{label}</div>}
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">
          {groups
            ? groups.map((g) => (
                <PalletCard key={g.rep} id={g.rep} data={data[g.rep]} count={g.ids.length} colorOf={colorOf} inTray={inTray}
                  onRotate={() => onRotate(g.rep)} onPeel={() => onPeel(g.rep)} onRemove={() => onRemove(g.rep)} />
              ))
            : ids.map((id) => (
                <PalletCard key={id} id={id} data={data[id]} colorOf={colorOf} inTray={inTray}
                  onRotate={() => onRotate(id)} onPeel={() => onPeel(id)} onRemove={() => onRemove(id)} />
              ))}
          {items.length === 0 && <div className="text-[11px] text-slate-400 text-center py-2">drop pallets here</div>}
          {!inTray && items.length > 0 && <LaneEnd listKey={listKey} />}
        </div>
      </SortableContext>
    </div>
  );
}

export default function LoadBoard({ open, onOpenChange, typeId, poId, poNumber, draftKey, seedContainers, allowRotation, topClearance, maxStackWeight, centerGap: centerGapProp, crossClearance, palletGap: palletGapProp }) {
  const [loading, setLoading] = useState(false);
  const [conts, setConts] = useState([]);        // [{id, container_type_id, label}]
  const [lists, setLists] = useState({ tray: [] }); // listKey -> [uid]
  const [data, setData] = useState({});          // uid -> {code, layers, rotated}
  const [board, setBoard] = useState(null);      // last plan-board response
  const [activeIdx, setActiveIdx] = useState(0);
  const [overflowMode, setOverflowMode] = useState("tray");
  const [enforceCapacity, setEnforceCapacity] = useState(false);
  const [margin, setMargin] = useState(palletGapProp != null ? palletGapProp : 2);
  const [centerGap, setCenterGap] = useState(centerGapProp != null ? centerGapProp : 4);
  // Inherit the auto planner's centre-gap each time the board is opened, so one setting drives both.
  useEffect(() => { if (open && centerGapProp != null) setCenterGap(centerGapProp); }, [open, centerGapProp]);
  useEffect(() => { if (open && palletGapProp != null) setMargin(palletGapProp); }, [open, palletGapProp]);
  const [savedList, setSavedList] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [originals, setOriginals] = useState({});   // label -> original {A,B} arrangement (from seed/open)
  const [seedBoard, setSeedBoard] = useState(null);  // full "before" board when opened from an auto-plan
  const [compareOn, setCompareOn] = useState(false); // side-by-side auto-plan vs edited
  const [audit, setAudit] = useState(null);          // Placement Auditor dialog data
  const [lastAudit, setLastAudit] = useState(null);  // last BLOCKED audit (for the persistent Auditor button)
  const seededRef = useRef(null);                    // signature of the last auto-plan we seeded from

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Prioritise a "stack here" drop zone when the pointer is literally over it (the zones are
  // small, so the default closestCorners often loses to the bigger parent card / lane).
  const collisionDetectionStrategy = (args) => {
    const stackHit = pointerWithin(args).find((c) => typeof c.id === "string" && c.id.startsWith("stack:"));
    return stackHit ? [stackHit] : closestCorners(args);
  };

  const colorMap = useMemo(() => {
    const all = (board?.containers || []).flatMap((c) => c.placement || []);
    return buildColorMap(all);
  }, [board]);
  const colorOf = (code) => colorMap[code] || "#64748b";

  const reset = () => { setConts([]); setLists({ tray: [] }); setData({}); setBoard(null); setActiveIdx(0); setSeedBoard(null); setCompareOn(false); seededRef.current = null; };

  const buildPayload = (cs = conts, ls = lists, d = data, enforce = enforceCapacity) => ({
    container_type_id: typeId,
    containers: cs.map((c) => ({
      container_type_id: c.container_type_id, label: c.label,
      arrangement: {
        A: (ls[`${c.id}:A`] || []).map((u) => ({ layers: d[u].layers, rotated: d[u].rotated })),
        B: (ls[`${c.id}:B`] || []).map((u) => ({ layers: d[u].layers, rotated: d[u].rotated })),
      },
    })),
    unloaded: (ls.tray || []).flatMap((u) => (d[u].layers || [d[u].code]).map((code) => ({ product_code: code }))),
    overflow_mode: overflowMode, enforce_capacity: enforce,
    allow_rotation: allowRotation, top_clearance_in: Number(topClearance) || 0,
    max_stack_weight_kg: Number(maxStackWeight) || 1600, pallet_margin_in: Number(margin),
    center_gap_in: Number(centerGap) || 0,
    cross_clearance_in: Number(crossClearance) || 0,
  });

  const syncFromResponse = (resp, snapshot = false) => {
    const nConts = []; const nLists = { tray: [] }; const nData = {};
    (resp.containers || []).forEach((c) => {
      const id = uid();
      nConts.push({ id, container_type_id: c.container_type_id, label: c.label });
      ["A", "B"].forEach((L) => {
        nLists[`${id}:${L}`] = ((c.arrangement && c.arrangement[L]) || []).map((col) => {
          const u = uid(); nData[u] = { code: col.layers[0], layers: [...col.layers], rotated: !!col.rotated }; return u;
        });
      });
    });
    (resp.unloaded || []).forEach((u2) => { const u = uid(); nData[u] = { code: u2.product_code, layers: [u2.product_code], rotated: false }; nLists.tray.push(u); });
    if (snapshot) {
      const orig = {};
      (resp.containers || []).forEach((c, i) => {
        const label = c.label || `Container ${i + 1}`;
        orig[label] = {
          A: ((c.arrangement && c.arrangement.A) || []).map((col) => ({ layers: [...col.layers], rotated: !!col.rotated })),
          B: ((c.arrangement && c.arrangement.B) || []).map((col) => ({ layers: [...col.layers], rotated: !!col.rotated })),
        };
      });
      setOriginals(orig);
    }
    setConts(nConts); setLists(nLists); setData(nData); setBoard(resp);
    setActiveIdx((a) => Math.min(a, Math.max(0, nConts.length - 1)));
  };

  const evaluate = async (payload, snapshot = false) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/container-planner/plan-board`, payload || buildPayload(), authH());
      syncFromResponse(r.data, snapshot);
      if (r.data.summary?.moved_to_overflow) {
        toast.warning(`${r.data.summary.moved_to_overflow} pallet(s) exceeded capacity → ${overflowMode === "new_container" ? "moved to a new container" : "unloaded tray"}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Board update failed"); }
    finally { setLoading(false); }
  };

  // Open the current auto-plan straight into the board (same path as a saved board),
  // capturing the untouched layout as the "before" for the compare panel + Restore.
  const seedFromContainers = async (containers) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/container-planner/plan-board`, {
        container_type_id: typeId, containers, unloaded: [], overflow_mode: overflowMode,
        enforce_capacity: false, allow_rotation: allowRotation, top_clearance_in: Number(topClearance) || 0,
        max_stack_weight_kg: Number(maxStackWeight) || 1600, pallet_margin_in: Number(margin), center_gap_in: Number(centerGap) || 0, cross_clearance_in: Number(crossClearance) || 0,
      }, authH());
      setEnforceCapacity(false);
      syncFromResponse(r.data, true);
      setSeedBoard(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the auto-plan on the board"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) { seededRef.current = null; return; }
    if (!(seedContainers && seedContainers.length)) return;
    const sig = JSON.stringify(seedContainers);
    if (seededRef.current === sig) return;
    seededRef.current = sig;
    seedFromContainers(seedContainers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedContainers]);

  const onSeed = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!/\.(xlsx|xlsm)$/i.test(file.name)) { toast.error("Upload the manual plan as an Excel (.xlsx)"); return; }
    setLoading(true);
    setSeedBoard(null); setCompareOn(false); seededRef.current = null;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/container-planner/seed-from-manual`, fd,
        { ...authH(), params: typeId ? { container_type_id: typeId } : {}, headers: { ...authH().headers, "Content-Type": "multipart/form-data" } });
      if (r.data.unmatched?.length) toast.warning(`${r.data.unmatched.length} code(s) not in catalog were skipped`);
      setEnforceCapacity(false);
      await evaluate({
        container_type_id: typeId,
        containers: r.data.containers.map((c) => ({ container_type_id: c.container_type_id, label: c.label, arrangement: c.arrangement })),
        unloaded: [], overflow_mode: overflowMode, enforce_capacity: false, allow_rotation: allowRotation,
        top_clearance_in: Number(topClearance) || 0, max_stack_weight_kg: Number(maxStackWeight) || 1600, pallet_margin_in: Number(margin), center_gap_in: Number(centerGap) || 0, cross_clearance_in: Number(crossClearance) || 0,
      }, true);
      toast.success(`Seeded ${r.data.container_count} container(s) — showing your exact manual plan`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not seed from the manual plan"); setLoading(false); }
  };

  // ---- mutations (each ends with an evaluate) ----
  const findKey = (id) => (id in lists ? id : Object.keys(lists).find((k) => lists[k].includes(id)));

  const rotatePallet = (id) => rotateChecked(id);

  // Unified rotate with a container-width feasibility test (item 5). Toggling rotation OFF
  // always fits; turning it ON runs a dry-run and BLOCKS if the rotated stack (plus the
  // overlapping opposite-lane stack) would exceed the container width.
  const rotateChecked = async (id) => {
    const key = findKey(id);
    if (!key || !data[id]) return;
    if (key === "tray") {
      const nd = { ...data, [id]: { ...data[id], rotated: !data[id].rotated } };
      setData(nd); return;
    }
    const [contId, lane] = key.split(":");
    const pos = (lists[key] || []).indexOf(id);
    const idx = conts.findIndex((c) => c.id === contId);
    const wasRotated = !!data[id].rotated;
    const nd = { ...data, [id]: { ...data[id], rotated: !wasRotated } };
    if (wasRotated) { setData(nd); evaluate(buildPayload(conts, lists, nd)); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/container-planner/plan-board`, buildPayload(conts, lists, nd), authH());
      const cont = (r.data.containers || [])[idx];
      const contW = cont?.container?.internal_width_in || 0;
      const pl = (cont?.placement || []).find((p) => p.lane === lane && p.arr_index === pos);
      if (pl && contW) {
        const opp = lane === "A" ? "B" : "A";
        const maxOppCross = (cont.placement || [])
          .filter((q) => q.lane === opp && q.start_in < pl.end_in - 0.5 && q.end_in > pl.start_in + 0.5)
          .reduce((m, q) => Math.max(m, q.cross_in || 0), 0);
        const gap = Number(centerGap) || 0;
        const clr = Number(crossClearance) || 0;
        const laneW = (contW - gap) / 2;
        // rotated-pair rule: when either side is wider than its half-width lane, the pair
        // must fit inside the container width + rotation clearance; otherwise the centre
        // channel must stay clear as before
        const oversize = pl.cross_in > laneW + 0.5 || maxOppCross > laneW + 0.5;
        const limit = (oversize ? contW - clr : (maxOppCross > 0.5 ? contW - gap : contW)) + 0.5;
        if (pl.cross_in + maxOppCross > limit) {
          toast.error(maxOppCross > 0.5
            ? `Can't rotate — combined width with the opposite lane would exceed ${(oversize ? contW - clr : contW - gap).toFixed(0)}in`
            : "Can't rotate — the pallet is too long to fit across the container width");
          return;
        }
      }
      syncFromResponse(r.data);
      if (r.data.summary?.moved_to_overflow) {
        toast.warning(`${r.data.summary.moved_to_overflow} pallet(s) exceeded capacity → ${overflowMode === "new_container" ? "moved to a new container" : "unloaded tray"}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Board update failed"); }
    finally { setLoading(false); }
  };

  // Rotate a stack by clicking it in the top-view diagram → map (lane, pos) to its pallet uid.
  const rotateStackFromDiagram = (lane, pos) => {
    if (!activeCont) return;
    const id = (lists[`${activeCont.id}:${lane}`] || [])[pos];
    if (id) rotateChecked(id);
  };
  // Commit a drag-to-stack, but first dry-run it and BLOCK if the resulting column would
  // exceed the container's usable height (roof clearance). No commit + error toast on block.
  // READ-ONLY Placement Auditor: explains the height/stacking block on demand.
  const runBoardAudit = async (payload) => {
    try {
      const r = await axios.post(`${API}/container-planner/audit-placement`, {
        container_type_id: activeCont?.container_type_id || typeId, allow_rotation: allowRotation,
        top_clearance_in: Number(topClearance) || 0, max_stack_weight_kg: Number(maxStackWeight) || 1600,
        pallet_margin_in: Number(margin) || 0, center_gap_in: Number(centerGap) || 0, cross_clearance_in: Number(crossClearance) || 0,
        ...payload,
      }, authH());
      if (r.data?.verdict === "blocked") setLastAudit(r.data);
      setAudit(r.data);
      return r.data;
    } catch { return null; }
  };
  const stackWithHeightCheck = async (nl, nd, tgtKey, tgt, baseLayers = [], candCode = null) => {
    const lane = tgtKey.split(":")[1];
    const index = (nl[tgtKey] || []).indexOf(tgt);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/container-planner/plan-board`, buildPayload(conts, nl, nd), authH());
      const cont = (r.data.containers || [])[activeIdx];
      const pl = (cont?.placement || []).find((p) => p.lane === lane && p.arr_index === index);
      if (pl && pl.headroom_in != null && pl.headroom_in < -0.1) {
        const h = cont?.container?.internal_height_in;
        toast.error(`Can't stack — the column would be ${pl.total_height_in}in tall and won't fit under the container roof${h ? ` (${h}in usable height)` : ""}`, {
          action: candCode ? { label: "Why it failed?", onClick: () => runBoardAudit({ target: { lane, base_layers: baseLayers }, candidate: { product_code: candCode } }) } : undefined,
        });
        return;
      }
      syncFromResponse(r.data);
      if (r.data.summary?.moved_to_overflow) {
        toast.warning(`${r.data.summary.moved_to_overflow} pallet(s) exceeded capacity → ${overflowMode === "new_container" ? "moved to a new container" : "unloaded tray"}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Board update failed"); }
    finally { setLoading(false); }
  };

  const peelPallet = (id) => {
    const layers = [...data[id].layers]; if (layers.length < 2) return;
    layers.pop();
    const trayUid = uid();
    const nd = { ...data, [id]: { ...data[id], layers }, [trayUid]: { code: data[id].code, layers: [data[id].code], rotated: false } };
    const nl = { ...lists, tray: [...lists.tray, trayUid] };
    setData(nd); setLists(nl); evaluate(buildPayload(conts, nl, nd));
  };
  const removePallet = (id) => {
    const key = findKey(id); if (!key) return;
    const nl = { ...lists };
    nl[key] = nl[key].filter((x) => x !== id);
    if (key === "tray") { const nd = { ...data }; delete nd[id]; setData(nd); setLists(nl); evaluate(buildPayload(conts, nl, nd)); return; }
    // send each pallet in the position to the tray
    const nd = { ...data };
    const trayAdd = (data[id].layers || [data[id].code]).map((code) => { const u = uid(); nd[u] = { code, layers: [code], rotated: false }; return u; });
    delete nd[id];
    nl.tray = [...nl.tray, ...trayAdd];
    setData(nd); setLists(nl); evaluate(buildPayload(conts, nl, nd));
  };

  const addContainer = () => {
    const id = uid();
    const nConts = [...conts, { id, container_type_id: typeId, label: null }];
    const nl = { ...lists, [`${id}:A`]: [], [`${id}:B`]: [] };
    setConts(nConts); setLists(nl); setActiveIdx(nConts.length - 1);
    evaluate(buildPayload(nConts, nl, data));
  };
  const deleteContainer = (cid) => {
    const nl = { ...lists };
    const moved = [...(nl[`${cid}:A`] || []), ...(nl[`${cid}:B`] || [])];
    delete nl[`${cid}:A`]; delete nl[`${cid}:B`];
    const nd = { ...data };
    moved.forEach((u) => { (nd[u].layers || [nd[u].code]).forEach((code, i) => { if (i === 0) { nd[u] = { code, layers: [code], rotated: false }; nl.tray.push(u); } else { const nu = uid(); nd[nu] = { code, layers: [code], rotated: false }; nl.tray.push(nu); } }); });
    const nConts = conts.filter((c) => c.id !== cid);
    setConts(nConts); setLists(nl); setData(nd); setActiveIdx((a) => Math.min(a, Math.max(0, nConts.length - 1)));
    evaluate(buildPayload(nConts, nl, nd));
  };

  // Item 7 — move EVERY pallet in a container to the unloaded tray, keeping the container.
  const unloadContainer = (cid) => {
    const nl = { ...lists }; const nd = { ...data };
    const moved = [...(nl[`${cid}:A`] || []), ...(nl[`${cid}:B`] || [])];
    if (!moved.length) { toast.info("This container is already empty"); return; }
    nl[`${cid}:A`] = []; nl[`${cid}:B`] = [];
    moved.forEach((u) => { (nd[u].layers || [nd[u].code]).forEach((code, i) => { if (i === 0) { nd[u] = { code, layers: [code], rotated: false }; nl.tray.push(u); } else { const nu = uid(); nd[nu] = { code, layers: [code], rotated: false }; nl.tray.push(nu); } }); });
    setLists(nl); setData(nd); evaluate(buildPayload(conts, nl, nd));
    toast.success("All pallets unloaded to the tray");
  };

  // Item 8 — restore a container to its ORIGINAL layout captured when the plan was seeded/opened.
  const restoreContainer = (cid) => {
    const idx = conts.findIndex((c) => c.id === cid);
    const label = board?.containers?.[idx]?.label || conts[idx]?.label || `Container ${idx + 1}`;
    const orig = originals[label];
    if (!orig) { toast.error("No original stuffing plan saved for this container"); return; }
    const nl = { ...lists }; const nd = { ...data };
    [...(nl[`${cid}:A`] || []), ...(nl[`${cid}:B`] || [])].forEach((u) => { delete nd[u]; });
    ["A", "B"].forEach((L) => {
      nl[`${cid}:${L}`] = (orig[L] || []).map((col) => { const u = uid(); nd[u] = { code: col.layers[0], layers: [...col.layers], rotated: !!col.rotated }; return u; });
    });
    // Conserve the plan's pallet set: the whole board has a fixed multiset of product codes
    // (sum of every container's original layout). After refilling this container, trim the
    // tray of any codes that now exceed the plan total — this reclaims the pallets that were
    // previously offloaded from it, so Unload-all → Restore can't leave duplicate copies behind.
    const planCount = {};
    Object.values(originals).forEach((o) => ["A", "B"].forEach((L) => (o[L] || []).forEach((col) => (col.layers || []).forEach((code) => { planCount[code] = (planCount[code] || 0) + 1; }))));
    const boardCount = {};
    Object.keys(nl).forEach((k) => { if (k === "tray") return; (nl[k] || []).forEach((u) => (nd[u].layers || [nd[u].code]).forEach((code) => { boardCount[code] = (boardCount[code] || 0) + 1; })); });
    const keptTray = []; const kept = {};
    (nl.tray || []).forEach((u) => {
      const code = nd[u].code;
      const allowed = planCount[code] ?? Infinity;
      if ((boardCount[code] || 0) + (kept[code] || 0) < allowed) { keptTray.push(u); kept[code] = (kept[code] || 0) + 1; }
      else { delete nd[u]; }
    });
    nl.tray = keptTray;
    setLists(nl); setData(nd); evaluate(buildPayload(conts, nl, nd));
    toast.success("Container restored to the original stuffing plan");
  };

  const autopack = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/container-planner/board-autopack`, buildPayload(), authH());
      syncFromResponse(r.data);
      toast.success(r.data.summary?.unloaded_count ? `Packed — ${r.data.summary.unloaded_count} still won't fit` : "All tray pallets packed into containers");
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-pack failed"); }
    finally { setLoading(false); }
  };

  const openSavedPicker = async () => {
    if (!poId && !draftKey) { toast.error("Open from a PO or draft to see saved boards"); return; }
    try {
      const r = await axios.get(`${API}/container-planner/splits`, { ...authH(), params: poId ? { po_id: poId } : { draft_key: draftKey } });
      setSavedList(r.data.splits || []);
      setShowSaved(true);
    } catch { toast.error("Could not load saved boards"); }
  };
  const openSaved = async (split) => {
    setShowSaved(false);
    setSeedBoard(null); setCompareOn(false); seededRef.current = null;
    const containers = (split.containers || []).filter((c) => c.arrangement).map((c) => ({
      container_type_id: c.container_type_id || typeId, label: c.label, arrangement: c.arrangement }));
    if (!containers.length) { toast.error("That saved plan has no layout to load"); return; }
    setEnforceCapacity(false);
    await evaluate({ container_type_id: typeId, containers, unloaded: [], overflow_mode: overflowMode,
      enforce_capacity: false, allow_rotation: allowRotation, top_clearance_in: Number(topClearance) || 0,
      max_stack_weight_kg: Number(maxStackWeight) || 1600, pallet_margin_in: Number(margin), center_gap_in: Number(centerGap) || 0, cross_clearance_in: Number(crossClearance) || 0 }, true);
    toast.success(`Opened “${split.name}”`);
  };

  const saveBoard = async () => {
    if (!poId && !draftKey) { toast.error("Open from a PO or draft to save the board"); return; }
    const name = window.prompt("Name this load plan:");
    if (!name) return;
    try {
      await axios.post(`${API}/container-planner/splits`, {
        po_id: poId || null, draft_key: poId ? null : draftKey, name, container_type_id: typeId,
        containers: buildPayload().containers.map((c) => ({ container_type_id: c.container_type_id, arrangement: c.arrangement, lines: [] })),
        summary: board?.summary || {},
      }, authH());
      toast.success(`Saved “${name}”`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const setEnforce = (v) => { setEnforceCapacity(v); evaluate(buildPayload(conts, lists, data, v)); };
  const setMarginVal = (v) => {
    const m = Math.max(0, Math.min(12, Number(v)));
    setMargin(m);
    if (conts.length) evaluate({ ...buildPayload(), pallet_margin_in: m });
  };
  const setCenterGapVal = (v) => {
    const g = Math.max(0, Math.min(24, Number(v)));
    setCenterGap(g);
    if (conts.length) evaluate({ ...buildPayload(), center_gap_in: g });
  };

  // ---- DnD ----
  const onDragEnd = ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    // drop onto a card's "stack here" zone → add this pallet as a top layer of that position
    if (typeof over.id === "string" && over.id.startsWith("stack:")) {
      const tgt = over.id.slice(6);
      if (tgt === active.id) return;
      const from = findKey(active.id);
      const tgtKey = findKey(tgt);
      if (!from || !tgtKey || tgtKey === "tray") return;
      const baseLayers = [...(data[tgt]?.layers || [])];   // Auditor: base column BEFORE the drop
      const dragged = data[active.id];
      const candCode = (dragged?.layers && dragged.layers[0]) || dragged?.code;   // pallet placed on top
      const nl = { ...lists }; const nd = { ...data };
      nl[from] = nl[from].filter((x) => x !== active.id);
      nd[tgt] = { ...nd[tgt], layers: [...nd[tgt].layers, ...(nd[active.id].layers || [nd[active.id].code])] };
      delete nd[active.id];
      stackWithHeightCheck(nl, nd, tgtKey, tgt, baseLayers, candCode);
      return;
    }
    const from = findKey(active.id);
    // explicit "end of lane" drop zone → place at the LAST position of that lane (item 3)
    if (typeof over.id === "string" && over.id.endsWith("::end")) {
      const dest = over.id.slice(0, -5);
      if (!from || !(dest in lists)) return;
      const nl = { ...lists };
      if (from === dest) {
        const arr = nl[from]; const oldI = arr.indexOf(active.id);
        if (oldI >= 0) nl[from] = arrayMove(arr, oldI, arr.length - 1);
      } else {
        nl[from] = nl[from].filter((x) => x !== active.id);
        nl[dest] = [...nl[dest], active.id];
      }
      setLists(nl); evaluate(buildPayload(conts, nl, data)); return;
    }
    let to = findKey(over.id) || (over.id in lists ? over.id : null);
    if (!from || !to) return;
    const nl = { ...lists };
    if (from === to) {
      const arr = nl[from];
      const oldI = arr.indexOf(active.id);
      const newI = over.id in lists ? arr.length - 1 : arr.indexOf(over.id);
      if (oldI !== newI && newI >= 0) nl[from] = arrayMove(arr, oldI, newI);
    } else {
      nl[from] = nl[from].filter((x) => x !== active.id);
      const destArr = [...nl[to]];
      const insertAt = over.id in lists ? destArr.length : Math.max(0, destArr.indexOf(over.id));
      destArr.splice(insertAt, 0, active.id);
      nl[to] = destArr;
    }
    setLists(nl);
    evaluate(buildPayload(conts, nl, data));
  };

  const started = conts.length > 0 || (lists.tray && lists.tray.length > 0);
  const activeCont = conts[activeIdx];
  const activePlan = board?.containers?.[activeIdx];
  const activeLabel = activePlan?.label || activeCont?.label || `Container ${activeIdx + 1}`;
  const hasOriginal = !!originals[activeLabel];

  // Item 6 — group identical single pallets in the tray into one chip with a count.
  const trayGroups = useMemo(() => {
    const groups = {}; const order = [];
    (lists.tray || []).forEach((u) => {
      const d = data[u]; if (!d) return;
      const key = `${d.code}|${(d.layers || [d.code]).join(",")}|${d.rotated ? 1 : 0}`;
      if (!groups[key]) { groups[key] = { rep: u, ids: [] }; order.push(key); }
      groups[key].ids.push(u);
    });
    return order.map((k) => groups[k]);
  }, [lists.tray, data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[94vh] p-0 overflow-hidden flex flex-col" data-testid="load-board-dialog">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-200 shrink-0">
          <DialogTitle className="flex items-center gap-2"><ContainerIcon className="w-5 h-5 text-blue-700" />Load Board {poNumber ? <span className="text-sm font-normal text-slate-400">· PO {poNumber}</span> : null}</DialogTitle>
          <DialogDescription>Opened from your auto-plan or a manual sheet — drag pallets between lanes, containers and the unloaded tray, rotate, stack, add or offload. Containers are never overloaded — anything that doesn't fit lands in the tray. Use <b>Compare to auto-plan</b> to see your edits beside the original.</DialogDescription>
        </DialogHeader>

        {/* toolbar */}
        <div className="px-5 py-2 border-b border-slate-200 flex flex-wrap items-center gap-2 shrink-0">
          <input id="board-seed-file" type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onSeed} data-testid="board-seed-input" />          <label htmlFor="board-seed-file" className="inline-flex items-center gap-1.5 cursor-pointer rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700" data-testid="board-seed-btn"><Upload className="w-4 h-4" />Seed from manual plan</label>
          <Button size="sm" variant="outline" onClick={addContainer} data-testid="board-add-container-btn"><Plus className="w-4 h-4 mr-1" />Add container</Button>
          <Button size="sm" variant="outline" onClick={autopack} disabled={!started || !(lists.tray?.length)} data-testid="board-autopack-btn"><Wand2 className="w-4 h-4 mr-1" />Auto-pack tray</Button>
          <div className="inline-flex items-center rounded-lg bg-slate-100 p-0.5 text-xs" data-testid="board-overflow-toggle">
            <span className="px-2 text-slate-500">Overflow →</span>
            <button onClick={() => setOverflowMode("tray")} className={`px-2 py-1 rounded-md ${overflowMode === "tray" ? "bg-white shadow-sm font-medium" : "text-slate-500"}`}>Tray</button>
            <button onClick={() => setOverflowMode("new_container")} className={`px-2 py-1 rounded-md ${overflowMode === "new_container" ? "bg-white shadow-sm font-medium" : "text-slate-500"}`}>New container</button>
          </div>
          <button onClick={() => setEnforce(!enforceCapacity)} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium border ${enforceCapacity ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`} data-testid="board-enforce-toggle" title="When on, pallets that exceed a container's capacity are moved out (to the tray or a new container).">
            <ShieldCheck className="w-4 h-4" />{enforceCapacity ? "Respecting capacity" : "Show exact plan"}
          </button>
          <div className="inline-flex items-center gap-1 text-xs text-slate-600" title="Handling gap added around each pallet for placement. Set to 0 to match your manual sheet's measurements.">
            <span>Gap</span>
            <input type="number" min="0" max="12" step="0.5" value={margin} onChange={(e) => setMarginVal(e.target.value)} className="w-14 rounded-md border border-slate-300 px-1.5 py-1 text-xs" data-testid="board-margin-input" />
            <span className="text-slate-400">in</span>
          </div>
          <div className="inline-flex items-center gap-1 text-xs text-slate-600" title="Minimum empty centre channel reserved down the middle between the Left and Right lanes (for dunnage / airbags). Rotations and placements must keep this gap clear.">
            <span>Centre gap</span>
            <input type="number" min="0" max="24" step="0.5" value={centerGap} onChange={(e) => setCenterGapVal(e.target.value)} className="w-14 rounded-md border border-slate-300 px-1.5 py-1 text-xs" data-testid="board-center-gap-input" />
            <span className="text-slate-400">in</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
            <button onClick={() => lastAudit && setAudit(lastAudit)} disabled={!lastAudit}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium border ${lastAudit ? "bg-white text-blue-700 border-blue-300 hover:bg-blue-50" : "bg-white text-slate-400 border-slate-200 cursor-not-allowed"}`}
              title={lastAudit ? "Open the report for the last blocked pallet" : "No blocked stacks yet — the report appears here after a stack is rejected"} data-testid="board-auditor-btn">
              <Search className="w-4 h-4" />Auditor{lastAudit ? <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-rose-100 text-rose-700">Step {lastAudit.blocked_step}</span> : null}
            </button>
            {seedBoard && (
              <button onClick={() => setCompareOn((v) => !v)} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium border ${compareOn ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300"}`} data-testid="board-compare-toggle" title="Compare your edits against the original auto-plan you opened.">
                <Columns2 className="w-4 h-4" />{compareOn ? "Comparing" : "Compare to auto-plan"}
              </button>
            )}
            {(poId || draftKey) && (
              <div className="relative">
                <Button size="sm" variant="outline" onClick={openSavedPicker} data-testid="board-open-saved-btn"><FolderOpen className="w-4 h-4 mr-1" />Open saved</Button>
                {showSaved && (
                  <div className="absolute right-0 top-full mt-1 z-50 w-72 max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg p-1" data-testid="board-saved-list">
                    <div className="flex items-center justify-between px-2 py-1 text-[11px] text-slate-400"><span>Saved load plans</span><button onClick={() => setShowSaved(false)} className="hover:text-slate-600"><X className="w-3.5 h-3.5" /></button></div>
                    {savedList.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-slate-400 text-center">No saved plans yet.</div>
                    ) : savedList.map((s) => (
                      <button key={s.id} onClick={() => openSaved(s)} className="w-full text-left rounded-md px-2 py-1.5 text-xs hover:bg-slate-50" data-testid={`board-saved-item-${s.id}`}>
                        <div className="font-medium text-slate-700 truncate">{s.name}</div>
                        <div className="text-[10px] text-slate-400">{s.container_count || (s.containers || []).length} container(s) · {new Date(s.updated_at || s.created_at).toLocaleDateString()}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {started && <Button size="sm" variant="outline" onClick={saveBoard} disabled={!poId && !draftKey} data-testid="board-save-btn"><Save className="w-4 h-4 mr-1" />Save</Button>}
            {started && <Button size="sm" variant="ghost" className="text-slate-500" onClick={reset} data-testid="board-reset-btn">Clear</Button>}
          </div>
        </div>

        <PlacementAuditor data={audit} onClose={() => setAudit(null)} />

        {!started ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 text-slate-500">
            <Package className="w-10 h-10 text-slate-300" />
            <div className="text-sm max-w-md">Upload your manual stuffing plan to load it onto the board, or add an empty container to start from scratch.</div>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={collisionDetectionStrategy} onDragStart={({ active }) => setActiveDrag(active.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveDrag(null)}>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-0 overflow-hidden">
              {/* LEFT: editor */}
              <div className="overflow-y-auto p-4 space-y-3 border-r border-slate-200" data-testid="board-editor">
                {/* container tabs */}
                <div className="flex flex-wrap gap-1.5">
                  {conts.map((c, i) => {
                    const p = board?.containers?.[i];
                    const over = p && !p.capacity_ok;
                    let overTxt = "";
                    if (p?.over_length) overTxt = `+${(p.used_length_in - (p.container?.internal_length_in || 0)).toFixed(1)}in`;
                    else if (p?.over_weight) {
                      const overKg = Math.round(p.total_weight_kg - (p.container?.max_payload_kg || 0));
                      overTxt = `+${Math.round(overKg * KG_TO_LB).toLocaleString()}lbs · ${overKg.toLocaleString()}kg`;
                    }
                    const q = p?.quality?.score;
                    const qtone = q == null ? "" : q >= 80 ? "bg-emerald-500 text-white" : q >= 60 ? "bg-amber-500 text-white" : "bg-rose-500 text-white";
                    return (
                      <button key={c.id} onClick={() => setActiveIdx(i)} className={`px-2.5 py-1 rounded-md text-xs font-medium border inline-flex items-center gap-1.5 ${i === activeIdx ? "bg-blue-700 text-white border-blue-700" : over ? "bg-white border-red-300 text-red-700" : "bg-white border-slate-300 text-slate-600"}`} data-testid={`board-tab-${i}`}>
                        {p?.label || `Container ${i + 1}`}<span className="opacity-70">{p?.total_pallets ?? 0}plt</span>
                        {q != null && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${qtone} ${i === activeIdx ? "ring-1 ring-white/70" : ""}`} title={`Load quality ${q}/100`} data-testid={`board-tab-quality-${i}`}>{q}</span>}
                        {over && <span className="inline-flex items-center gap-0.5 text-red-600" data-testid={`board-tab-overby-${i}`}><AlertTriangle className="w-3 h-3" />{overTxt}</span>}
                      </button>
                    );
                  })}
                </div>

                {!enforceCapacity && board?.summary?.any_over && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-xs text-amber-800" data-testid="board-overcap-banner">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span className="flex-1">You're viewing the exact manual plan — one or more containers are slightly over the engine's length allowance (shown in red). Set <b>Gap</b> to 0 to match your manual sheet, or click to unload the excess.</span>
                    <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700" onClick={() => setEnforce(true)} data-testid="board-unload-excess-btn">Unload excess</Button>
                  </div>
                )}

                {activeCont && (
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="board-active-container">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-slate-800">{activePlan?.label || `Container ${activeIdx + 1}`}</div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-slate-500 hover:text-amber-700 hover:bg-amber-50" onClick={() => unloadContainer(activeCont.id)} data-testid="board-unload-container-btn" title="Move every pallet in this container to the unloaded tray"><ArrowDownToLine className="w-3.5 h-3.5 mr-1" />Unload all</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-slate-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => restoreContainer(activeCont.id)} disabled={!hasOriginal} data-testid="board-restore-container-btn" title={hasOriginal ? "Reset this container to the originally uploaded stuffing plan" : "No original layout captured"}><RotateCcw className="w-3.5 h-3.5 mr-1" />Restore</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteContainer(activeCont.id)} data-testid="board-delete-container-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                      </div>
                    </div>
                    {activePlan && (
                      <div className="flex items-center gap-3">
                        <Meter label={<span className="inline-flex items-center gap-1"><span>Weight</span><span className="text-amber-500 font-mono tabular-nums">{fmt(Math.round(activePlan.total_weight_kg * KG_TO_LB))}/{fmt(Math.round((activePlan.container?.max_payload_kg || 0) * KG_TO_LB))}<span className="text-[9px] uppercase tracking-wider opacity-80 ml-0.5">lbs</span></span><span className="text-slate-400">·</span><span className="text-sky-500 font-mono tabular-nums">{fmt(activePlan.total_weight_kg)}/{fmt(activePlan.container?.max_payload_kg)}<span className="text-[9px] uppercase tracking-wider opacity-80 ml-0.5">kg</span></span></span>} pct={(activePlan.total_weight_kg / (activePlan.container?.max_payload_kg || 1)) * 100} over={activePlan.over_weight} />
                        <Meter label={`Length ${fmt(activePlan.used_length_in)}/${fmt(activePlan.container?.internal_length_in)} in`} pct={(activePlan.used_length_in / (activePlan.container?.internal_length_in || 1)) * 100} over={activePlan.over_length} />
                      </div>
                    )}
                    {activePlan?.warnings?.length > 0 && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{activePlan.warnings.join(" ")}</div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Lane listKey={`${activeCont.id}:A`} ids={lists[`${activeCont.id}:A`] || []} data={data} colorOf={colorOf} label="Left lane" onRotate={rotatePallet} onPeel={peelPallet} onRemove={removePallet} />
                      <Lane listKey={`${activeCont.id}:B`} ids={lists[`${activeCont.id}:B`] || []} data={data} colorOf={colorOf} label="Right lane" onRotate={rotatePallet} onPeel={peelPallet} onRemove={removePallet} />
                    </div>
                  </div>
                )}

                {/* unloaded tray */}
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="board-tray">
                  <div className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5"><Package className="w-4 h-4" />Unloaded pallets <Badge className="bg-amber-500 text-white h-5">{lists.tray?.length || 0}</Badge></div>
                  <Lane listKey="tray" ids={lists.tray || []} groups={trayGroups} data={data} colorOf={colorOf} onRotate={rotatePallet} onPeel={peelPallet} onRemove={removePallet} inTray />
                </div>
              </div>

              {/* RIGHT: live diagram (pinned) */}
              <div className="overflow-y-auto p-4 bg-slate-50" data-testid="board-diagram-pane">
                {compareOn && seedBoard ? (
                  <div className="space-y-3" data-testid="board-compare-pane">
                    {(() => {
                      const beforePlan = (seedBoard.containers || []).find((c) => (c.label || "") === activeLabel) || seedBoard.containers?.[activeIdx];
                      const bPlt = beforePlan?.total_pallets ?? 0;
                      const aPlt = activePlan?.total_pallets ?? 0;
                      const diff = aPlt - bPlt;
                      return (
                        <>
                          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="board-compare-stats">
                            <span className="font-semibold flex items-center gap-1.5"><Columns2 className="w-4 h-4" />Auto-plan vs your edits · {activeLabel}</span>
                            <span>Auto-plan: <b>{bPlt}</b> plt</span>
                            <span>Your edits: <b>{aPlt}</b> plt {diff !== 0 && <span className={diff > 0 ? "text-emerald-700" : "text-rose-700"}>({diff > 0 ? "+" : ""}{diff})</span>}</span>
                            <span>Containers: <b>{conts.length}</b> · Unloaded tray: <b>{lists.tray?.length || 0}</b></span>
                          </div>
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Original auto-plan</div>
                              <QualityCard quality={beforePlan?.quality} testidPrefix="board-before" />
                              <WeightDistribution wd={beforePlan?.weight_distribution} testidPrefix="board-before" />
                              <LoadDiagram plan={beforePlan} cmap={colorMap} />
                            </div>
                            <div className="space-y-2">
                              <div className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider">Your edits (live)</div>
                              <QualityCard quality={activePlan?.quality} testidPrefix="board-after" />
                              <WeightDistribution wd={activePlan?.weight_distribution} testidPrefix="board-after" />
                              <LoadDiagram plan={activePlan} onRotateStack={rotateStackFromDiagram} cmap={colorMap} />
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="board-diagram-stack">
                    <QualityCard quality={activePlan?.quality} testidPrefix="board" />
                    <WeightDistribution wd={activePlan?.weight_distribution} testidPrefix="board" />
                    <LoadDiagram plan={activePlan} onRotateStack={rotateStackFromDiagram} cmap={colorMap} />
                  </div>
                )}
              </div>
            </div>

            <DragOverlay>
              {activeDrag && data[activeDrag] ? (
                <div className="flex items-center gap-1.5 rounded-md border border-blue-400 bg-white px-2 py-1.5 text-xs shadow-lg">
                  <span className="w-3 h-3 rounded-sm" style={{ background: colorOf(data[activeDrag].code) }} />
                  <span className="font-medium text-slate-700">{data[activeDrag].code}</span>
                  {data[activeDrag].layers?.length > 1 && <Badge variant="outline" className="h-4 px-1 text-[10px]">×{data[activeDrag].layers.length}</Badge>}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </DialogContent>
    </Dialog>
  );
}
